import { useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { PORTAL_PARENT_CLASS, PortalContext, type PortalContextProps } from '../contexts/PortalContext';

/**
 * If this provider is nested below another `PortalContextProvider`,
 * any calls to `getElement` will call `getElement` on the parent context
 * if that element is not defined in this context.
 *
 * This ensures that we can always access portals defined at the top level,
 * yet still allows us to contextually override a certain portal
 * (e.g. "scope" the definition of the blotter table filters portal to a given panel).
 */
export function PortalContextProvider({ children }: { children?: ReactNode | undefined }) {
  const parentProvider = useContext(PortalContext);
  const [portals, setPortals] = useState<Map<string, HTMLElement | null>>(new Map());

  const setElement = useCallback<PortalContextProps['setElement']>(
    <T extends HTMLElement>(portalId: string, node: T | null) => {
      setPortals(prevPortals => new Map([...prevPortals.entries(), [portalId, node]]));
    },
    []
  );

  const getElement = useCallback<PortalContextProps['getElement']>(
    <T extends HTMLElement>(portalId: string, createTopLevelPortal: boolean) => {
      const portalElement = portals.get(portalId) ?? parentProvider?.getElement?.(portalId, createTopLevelPortal);
      if (portalElement != null) {
        return portalElement as T;
      } else {
        // If there's no registered element, create a top level one
        const portalElement = getTopLevelPortalElement(portalId, createTopLevelPortal);
        return portalElement as T;
      }
    },
    [parentProvider, portals]
  );

  const value = useMemo<PortalContextProps>(
    () => ({
      getElement,
      setElement,
    }),
    [getElement, setElement]
  );
  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

const getTopLevelPortalElement = (portalId: string, createTopLevelPortal: boolean): HTMLElement | undefined => {
  let parentElem = document.querySelector<HTMLDivElement>(`#${CSS.escape(portalId)}`);
  if (parentElem) {
    return parentElem;
  }

  if (!createTopLevelPortal) {
    return undefined;
  }

  // Create the parent element for the portal
  parentElem = document.createElement('div');
  parentElem.setAttribute('id', portalId);
  parentElem.setAttribute('data-testid', portalId);

  // Simple workaround for preventing custom date filter to close when selecting a date:
  // @see https://www.ag-grid.com/react-grid/component-filter/#custom-filters-containing-a-popup-element
  // Also, set a unique class attribute on the talos portals such that other elements can figure out if they're within a portal
  parentElem.setAttribute('class', `ag-custom-component-popup ${PORTAL_PARENT_CLASS}`);

  document.body.insertBefore(parentElem, document?.body?.lastElementChild?.nextElementSibling ?? null);
  return parentElem;
};
